import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RefreshCw, Link2, Video, Star, Loader2 } from "lucide-react";
import { getFavoriteRecipes } from "@/lib/mealPlan";
import { ScrollArea } from "@/components/ui/scroll-area";

interface ChangeMealDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerateNew: () => void;
  onImportFromUrl: (url: string) => Promise<void>;
  onImportFromVideo: (url: string) => void;
  onSelectFavorite?: (meal: any) => void;
}

export function ChangeMealDialog({
  isOpen,
  onClose,
  onGenerateNew,
  onImportFromUrl,
  onImportFromVideo,
  onSelectFavorite,
}: ChangeMealDialogProps) {
  const [url, setUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [favorites, setFavorites] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [importProgress, setImportProgress] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadFavorites();
    }
  }, [isOpen]);

  const loadFavorites = async () => {
    try {
      setLoadingFavorites(true);
      const data = await getFavoriteRecipes();
      setFavorites(data);
    } catch (error) {
      console.error("Error loading favorites:", error);
    } finally {
      setLoadingFavorites(false);
    }
  };

  const handleImportFromUrl = useCallback(async () => {
    if (!url.trim()) return;

    try {
      setLoading(true);
      setError(null);
      setImportProgress("מתחיל בייבוא המתכון...");
      await onImportFromUrl(url);
      setUrl("");
      onClose();
    } catch (err: unknown) {
      console.error('ChangeMealDialog error:', err);
      setError(
        err instanceof Error ? err.message : "Failed to import recipe"
      );
    } finally {
      setLoading(false);
      setImportProgress("");
    }
  }, [url, onImportFromUrl, onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>החלף ארוחה</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="generate" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="generate" className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              צור חדש
            </TabsTrigger>
            <TabsTrigger value="favorites" className="flex items-center gap-2">
              <Star className="h-4 w-4" />
              מועדפים
            </TabsTrigger>
            <TabsTrigger value="url" className="flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              ייבא מקישור
            </TabsTrigger>
            <TabsTrigger value="video" className="flex items-center gap-2">
              <Video className="h-4 w-4" />
              ייבא מסרטון
            </TabsTrigger>
          </TabsList>

          <TabsContent value="generate" className="mt-4">
            <Card className="p-4">
              <p className="text-sm text-muted-foreground mb-4">
                נייצר 3 אפשרויות חדשות בהתאם להעדפות שלך
              </p>
              <Button onClick={onGenerateNew} className="w-full">
                <RefreshCw className="h-4 w-4 ml-2" />
                צור אפשרויות חדשות
              </Button>
            </Card>
          </TabsContent>

          <TabsContent value="favorites" className="mt-4">
            <Card className="p-4">
              {loadingFavorites ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : favorites.length > 0 ? (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-4">
                    {favorites.map((favorite) => (
                      <Card
                        key={favorite.id}
                        className="p-4 hover:bg-accent transition-colors cursor-pointer"
                        onClick={() => onSelectFavorite?.(favorite.recipe)}
                      >
                        <div className="space-y-2">
                          <h3 className="text-lg font-medium text-right">
                            {favorite.recipe.name}
                          </h3>
                          <p className="text-sm text-muted-foreground text-right">
                            {favorite.recipe.description}
                          </p>
                        </div>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <p className="text-center text-muted-foreground">
                  לא נמצאו מתכונים מועדפים
                </p>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="url" className="mt-4">
            <Card className="p-4 space-y-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  הכנס קישור למתכון מאתר מתכונים
                </p>
                {importProgress && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{importProgress}</span>
                  </div>
                )}
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <Input
                  placeholder="https://..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  dir="ltr"
                  disabled={loading}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleImportFromUrl();
                    }
                  }}
                />
              </div>
              <Button
                onClick={handleImportFromUrl}
                className="w-full"
                disabled={!url.trim() || loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Link2 className="h-4 w-4 ml-2" />
                    ייבא מתכון
                  </>
                )}
              </Button>
            </Card>
          </TabsContent>

          <TabsContent value="video" className="mt-4">
            <Card className="p-4 space-y-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  הכנס קישור לסרטון מתכון (YouTube, Instagram, TikTok)
                </p>
                <Input
                  placeholder="https://..."
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                />
              </div>
              <Button
                onClick={() => onImportFromVideo(videoUrl)}
                className="w-full"
                disabled={!videoUrl}
              >
                <Video className="h-4 w-4 ml-2" />
                ייבא מסרטון
              </Button>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
